'use client';
import React from 'react';
import {
  TwitterShareButton,
  EmailShareButton,
  FacebookShareButton,
  LinkedinShareButton,
  WhatsappShareButton,
  FacebookIcon,
  EmailIcon,
  LinkedinIcon,
  BlueskyShareButton,
  XIcon,
  BlueskyIcon,
} from 'react-share';
import { runningClientSide, formatTwitterText, appendQueryParams, formatBlueSkyText } from '@benzinga/utils';
import styled from '@benzinga/themetron';
import { Icon, Tooltip } from '@benzinga/core-ui';
import { faLink } from '@fortawesome/pro-solid-svg-icons/faLink';

import Hooks from '@benzinga/hooks';
import { TrackingManager } from '@benzinga/tracking-manager';
import { appEnvironment, appName } from '@benzinga/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLinkedin,
  faSquareFacebook,
  faSquareWhatsapp,
  faSquareXTwitter,
  faSquareBluesky,
} from '@fortawesome/free-brands-svg-icons';
import { faEnvelopeSquare } from '@fortawesome/pro-solid-svg-icons';
import { SessionContext } from '@benzinga/session-context';

type ShareButtonType = 'whatsapp' | 'linkedin' | 'twitter' | 'facebook' | 'email' | 'copy' | 'bluesky';

export interface ShareButtonsProps {
  className?: string;
  tickers?: string[];
  title?: string;
  url?: string;
  showFontAwesomeIcons?: boolean;
  /**
   * @deprecated Use showButtons instead
   */
  showWhatsAppIcon?: boolean;
  buttonStyle?: Omit<React.SVGProps<SVGSVGElement>, 'width' | 'height'> & {
    bgStyle?: React.CSSProperties | undefined;
    borderRadius?: number | undefined;
    iconFillColor?: string | undefined;
    round?: boolean | undefined;
    size?: string | number | undefined;
  };
  showButtons?: ShareButtonType[];
  utmSource?: string;
}

export const ShareButtons: React.FC<ShareButtonsProps> = React.memo(
  ({
    buttonStyle = {},
    showButtons = ['linkedin', 'twitter', 'facebook', 'bluesky', 'email', 'copy'],
    showFontAwesomeIcons,
    showWhatsAppIcon,
    tickers,
    title = '',
    url,
    utmSource,
  }) => {
    const session = React.useContext(SessionContext);
    const [shareUrl, setShareUrl] = React.useState(url ? appendQueryParams(url, { utm_source: utmSource }) : url);
    const [shareTitle, setShareTitle] = React.useState(title);
    const { handler } = Hooks.useSocialShare({ url: shareUrl });
    const [tooltipVisible, setTooltipVisible] = React.useState(false);

    // TODO: Remove later - Handles legacy showWhatsAppIcon prop
    React.useEffect(() => {
      if (showWhatsAppIcon && !showButtons.includes('whatsapp')) {
        showButtons.push('whatsapp');
      }
    }, [showWhatsAppIcon, showButtons]);

    React.useEffect(() => {
      if (!runningClientSide()) return;
      if (!url) setShareUrl(appendQueryParams(window.location.href, { utm_source: utmSource }));
      if (!title) setShareTitle(window.document.title);
    }, [url, title, utmSource]);

    React.useEffect(() => {
      if (!tooltipVisible) return;
      const timer = window.setTimeout(() => setTooltipVisible(false), 2500);
      return () => window.clearTimeout(timer);
    }, [tooltipVisible]);

    const onShareClick = React.useCallback(
      (medium: string) => {
        session.getManager(TrackingManager).trackLinkEvent('click', {
          link_id: medium,
          link_type: 'social',
          link_url: shareUrl,
          outbound_ad_url: shareUrl,
          value: medium,
        });
      },
      [session, shareUrl],
    );

    const handleCopyLink = React.useCallback(() => {
      const success = handler('Copy');
      if (success) setTooltipVisible(true);
      onShareClick('Copy Link');
    }, [handler, onShareClick]);

    const shareText = `${shareTitle} ${shareUrl}`;

    const via = appEnvironment().isApp(appName.india) ? 'BenzingaIndia' : 'Benzinga';

    if (!shareUrl) {
      return <div />;
    }

    return (
      <Container className="page-share-buttons">
        {showButtons.includes('whatsapp') && (
          <WhatsappShareButton aria-label="Share on WhatsApp" onClick={() => onShareClick('WhatsApp')} url={shareUrl}>
            <FontAwesomeIcon icon={faSquareWhatsapp} size="2xl" style={{ color: '#25D366' }} />
          </WhatsappShareButton>
        )}
        {showButtons.includes('linkedin') && (
          <LinkedinShareButton
            aria-label="Share on Linkedin"
            onClick={() => onShareClick('Linkedin')}
            source="Benzinga"
            title={shareTitle}
            url={shareUrl}
          >
            {showFontAwesomeIcons ? (
              <FontAwesomeIcon icon={faLinkedin} size="2xl" style={{ color: '#0e76a8' }} />
            ) : (
              <LinkedinIcon size={30} {...(buttonStyle || {})} />
            )}
          </LinkedinShareButton>
        )}
        {showButtons.includes('twitter') && (
          <TwitterShareButton
            aria-label="Share on X"
            onClick={() => onShareClick('Twitter')}
            title={formatTwitterText({ text: shareTitle, tickers })}
            url={shareUrl}
            via={via}
          >
            {showFontAwesomeIcons ? (
              <FontAwesomeIcon icon={faSquareXTwitter} size="2xl" />
            ) : (
              <XIcon size={30} {...(buttonStyle || {})} />
            )}
          </TwitterShareButton>
        )}
        {showButtons.includes('facebook') && (
          <FacebookShareButton aria-label="Share on Facebook" onClick={() => onShareClick('Facebook')} url={shareUrl}>
            {showFontAwesomeIcons ? (
              <FontAwesomeIcon icon={faSquareFacebook} size="2xl" style={{ color: '#1877f2' }} />
            ) : (
              <FacebookIcon size={30} {...(buttonStyle || {})} />
            )}
          </FacebookShareButton>
        )}
        {showButtons.includes('bluesky') && (
          <BlueskyShareButton
            aria-label="Share on Bluesky"
            onClick={() => onShareClick('Bluesky')}
            title={formatBlueSkyText(shareTitle)}
            url={shareUrl}
          >
            {showFontAwesomeIcons ? (
              <FontAwesomeIcon icon={faSquareBluesky} size="2xl" style={{ color: '#007bff' }} />
            ) : (
              <BlueskyIcon size={30} {...(buttonStyle || {})} />
            )}
          </BlueskyShareButton>
        )}
        {showButtons.includes('email') && (
          <EmailShareButton
            aria-label="Share on Email"
            body={shareText}
            onClick={undefined}
            openShareDialogOnClick={true}
            subject={shareTitle}
            url={shareUrl}
          >
            <div onClick={() => onShareClick('Email')}>
              {showFontAwesomeIcons ? (
                <FontAwesomeIcon icon={faEnvelopeSquare} size="2xl" />
              ) : (
                <EmailIcon size={30} {...(buttonStyle || {})} />
              )}
            </div>
          </EmailShareButton>
        )}
        {showButtons.includes('copy') && (
          <Tooltip content="Link copied!" size="sm" trigger="click" visible={tooltipVisible}>
            <CopyLinkButton
              $backgroundColor={buttonStyle?.bgStyle?.fill}
              $color={buttonStyle?.iconFillColor}
              aria-label={shareTitle ? `Copy link to ${shareTitle}` : 'Copy link'}
              className={`copy-link-button ${showFontAwesomeIcons ? 'bordered-square' : ''}`}
              onClick={handleCopyLink}
            >
              <Icon className="link-icon" icon={faLink} />
            </CopyLinkButton>
          </Tooltip>
        )}
      </Container>
    );
  },
);

const CopyLinkButton = styled.button<{ $backgroundColor?: string; $color?: string }>`
  height: 30px;
  width: 30px;
  background-color: ${({ $backgroundColor }) => ($backgroundColor ? $backgroundColor : '#7f7f7f')};
  display: flex;
  align-items: center;
  justify-content: center;

  .link-icon {
    color: ${({ $color }) => ($color ? $color : '#ffffff')};
  }

  &.bordered-square {
    height: 29px;
    width: 28px;
    background-color: #000c;
    border-radius: 4px;
  }
`;

const Container = styled.div`
  &.page-share-buttons {
    display: flex;
    align-items: center;
    position: relative;
    height: 40px;
    gap: 0 5px;

    > button,
    .copy-link-button {
      transition: transform 250ms;
      will-change: transform;

      &:hover {
        transform: translateY(-5px);
      }
    }
  }
`;
